import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/Button';
import Card from '../components/Card';
import SectionTitle from '../components/SectionTitle';
import sessionStore from '../services/sessionStore';

const SessionHistoryPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    limit: 20,
    offset: 0
  });
  const [summary, setSummary] = useState(null);

  // Load sessions (stored locally in this browser)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [sessionsResponse, summaryResponse] = await Promise.all([
          sessionStore.getSessionHistory(filters),
          sessionStore.getSessionSummary()
        ]);

        if (sessionsResponse.success) {
          setSessions(sessionsResponse.data || []);
        } else {
          throw new Error(sessionsResponse.message || 'Failed to load sessions');
        }

        if (summaryResponse.success) {
          setSummary(summaryResponse.data);
        }

      } catch (error) {
        console.error('Error loading session data:', error);
        setError('Failed to load session history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset pagination when filters change
    }));
  };

  // Handle session continuation
  const handleContinueSession = async (sessionId) => {
    try {
      const response = await sessionStore.continueSession(sessionId);
      if (response.success) {
        // Navigate to chat page with session data
        navigate('/priorv-chat', {
          state: {
            sessionId,
            formData: response.data.formData,
            messages: response.data.messages,
            continuing: true
          }
        });
      } else {
        alert(response.message || 'Failed to continue session');
      }
    } catch (error) {
      console.error('Error continuing session:', error);
      alert('Failed to continue session. Please try again.');
    }
  };

  // Handle status update
  const handleStatusUpdate = async (sessionId, newStatus) => {
    try {
      const response = await sessionStore.updateSessionStatus(sessionId, newStatus);
      if (response.success) {
        // Refresh sessions
        const sessionsResponse = await sessionStore.getSessionHistory(filters);
        if (sessionsResponse.success) {
          setSessions(sessionsResponse.data || []);
        }
      } else {
        alert(response.message || 'Failed to update session status');
      }
    } catch (error) {
      console.error('Error updating session status:', error);
      alert('Failed to update session status. Please try again.');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="page">
      <Header />

      {/* Session History Hero Section */}
      <section className="hero session-history-hero" style={{
        background: `linear-gradient(135deg, rgba(52, 152, 219, 0.85) 0%, rgba(39, 174, 96, 0.85) 100%)`,
        color: 'white'
      }}>
        <div className="hero-content">
          <h1 className="hero-title">Session History</h1>
          <p className="hero-description">View and manage your doctor visit preparation sessions</p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container section">
        <div className="session-history-content">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="summary-card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{summary.total_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Total Sessions</div>
                </div>
              </Card>
              <Card className="summary-card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{summary.active_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Active Sessions</div>
                </div>
              </Card>
              <Card className="summary-card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{summary.completed_sessions || 0}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
              </Card>
              <Card className="summary-card">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{summary.recent_sessions || 0}</div>
                  <div className="text-sm text-gray-600">This Week</div>
                </div>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="filters-card mb-6">
            <h3 className="text-lg font-semibold mb-4">Filter Sessions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="statusFilter"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label htmlFor="limitFilter" className="block text-sm font-medium text-gray-700 mb-1">
                  Show
                </label>
                <select
                  id="limitFilter"
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={10}>10 sessions</option>
                  <option value={20}>20 sessions</option>
                  <option value={50}>50 sessions</option>
                </select>
              </div>
            </div>
          </Card>

          {error && (
            <div className="error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Sessions List */}
          <div className="sessions-header mb-4">
            <SectionTitle
              title="Your Sessions"
              subtitle="View and continue your doctor visit preparation sessions"
            />
          </div>

          {loading ? (
            <div className="loading-indicator text-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4">Loading sessions...</p>
            </div>
          ) : sessions.length > 0 ? (
            <div className="sessions-grid space-y-4">
              {sessions.map(session => (
                <Card key={session.id} className="session-card">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {session.chief_complaint || 'General Visit Preparation'}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {session.status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Doctor Specialty:</strong> {session.doctor_specialty || 'Not specified'}</p>
                        <p><strong>Created:</strong> {formatDate(session.created_at)}</p>
                        <p><strong>Messages:</strong> {session.message_count || 0}</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleContinueSession(session.session_id)}
                      >
                        Continue
                      </Button>
                      
                      {session.status === 'active' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleStatusUpdate(session.session_id, 'completed')}
                        >
                          Mark Complete
                        </Button>
                      )}
                      
                      {session.status === 'completed' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleStatusUpdate(session.session_id, 'archived')}
                        >
                          Archive
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="empty-state text-center p-8">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.405L3 21l2.595-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
              <p className="text-gray-500 mb-4">
                {Object.values(filters).some(v => v) 
                  ? 'No sessions match your current filters. Try adjusting the filters above.'
                  : 'You haven\'t started any doctor visit preparation sessions yet.'
                }
              </p>
              <Button variant="primary" to="/priorv-form">
                Start New Session
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SessionHistoryPage;
